/**
 * CognitiveBiasPanel
 *
 * Inline panel used within session results, cycle overviews, and item detail
 * pages. Shows a loading state while the LLM runs, then renders detected biases
 * grouped by severity. Self-contained — manages its own fetch lifecycle.
 */

import { useState, useEffect, useCallback } from 'react';
import { Brain, RefreshCw, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { detectCognitiveBiases } from '../../../lib/llmCognitiveBiasService';
import { BiasInsightCard } from './BiasInsightCard';
import type {
  BiasAnalysisContext,
  BiasDetectionResult,
  BiasSeverity,
} from '../../../types/prioritization/cognitiveBias';

interface CognitiveBiasPanelProps {
  context: BiasAnalysisContext;
  /** If true, auto-runs detection on mount */
  autoRun?: boolean;
  /** Label used in the header (e.g. "Session Results", "Cycle Review") */
  contextLabel?: string;
}

const RISK_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  high:   { bg: 'bg-[var(--coral-dim)] border-[var(--coral)]',    text: 'text-[var(--coral)]',    dot: 'bg-[var(--coral)]' },
  medium: { bg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]', text: 'text-[var(--mustard)]',  dot: 'bg-[var(--mustard)]' },
  low:    { bg: 'bg-[var(--slate-dim)] border-[var(--slate)]',   text: 'text-[var(--text-sec)]',   dot: 'bg-[var(--slate)]' },
  none:   { bg: 'bg-[var(--green-dim)] border-[var(--green)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]',  dot: 'bg-[var(--green)]' },
};

function RiskBadge({ level }: { level: BiasSeverity | 'none' }) {
  const s = RISK_STYLES[level] ?? RISK_STYLES.none;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${s.bg} ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      {level === 'none' ? 'No biases detected' : `${level.charAt(0).toUpperCase() + level.slice(1)} bias risk`}
    </span>
  );
}

export function CognitiveBiasPanel({
  context,
  autoRun = false,
  contextLabel,
}: CognitiveBiasPanelProps) {
  const [result, setResult] = useState<BiasDetectionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detection = await detectCognitiveBiases(context);
      setResult(detection);
      setHasRun(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The bias check could not run. Try again.');
      console.warn('[CognitiveBiasPanel]', e);
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => {
    if (autoRun && !hasRun) run();
  }, [autoRun, hasRun, run]);

  const label = contextLabel ?? result?.contextLabel ?? 'Analysis';

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-[var(--slate-dim)] flex items-center justify-center flex-shrink-0">
            <Brain className="h-4 w-4 text-[var(--text-ter)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--text-pri)]">Cognitive Bias Check</p>
            <p className="text-xs text-[var(--text-ter)]">{label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result && <RiskBadge level={result.overallRiskLevel} />}
          {hasRun && !loading && (
            <button
              type="button"
              onClick={run}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              title="Re-run analysis"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          )}
          {hasRun && (
            <button
              type="button"
              onClick={() => setCollapsed(c => !c)}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              aria-label={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      {!hasRun && !loading && (
        <div className="px-5 py-6 flex flex-col items-center gap-3 text-center">
          <div className="w-10 h-10 rounded-full bg-[var(--slate-dim)] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-[var(--text-ter)]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[var(--text-sec)]">Scan for decision biases</p>
            <p className="text-xs text-[var(--text-ter)] mt-0.5 max-w-xs">
              AI analyzes your items, scores, and group patterns to surface cognitive biases that may be shaping this outcome.
            </p>
          </div>
          <button
            type="button"
            onClick={run}
            className="mt-1 inline-flex items-center gap-1.5 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium px-4 py-2 transition-colors"
          >
            <Brain className="h-4 w-4" />
            Run Bias Check
          </button>
        </div>
      )}

      {loading && (
        <div className="px-5 py-8 flex flex-col items-center gap-3 text-center">
          <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center animate-pulse">
            <Brain className="h-4 w-4 text-[var(--text-ter)]" />
          </div>
          <p className="text-sm text-[var(--text-ter)]">Analyzing decision patterns…</p>
          <p className="text-xs text-[var(--text-ter)]">Checking for groupthink, recency bias, sunk costs, and more</p>
        </div>
      )}

      {error && (
        <div className="px-5 py-4 bg-[var(--coral-dim)] border-t border-[var(--coral)]">
          <p className="text-sm text-[var(--coral)]">{error}</p>
          <button
            type="button"
            onClick={run}
            className="mt-2 text-xs text-[var(--coral)] underline hover:text-[var(--coral)]"
          >
            Try again
          </button>
        </div>
      )}

      {!collapsed && result && !loading && (
        <div className="px-5 py-4 space-y-3">
          {/* Summary */}
          {result.summary && (
            <p className="text-sm text-[var(--text-sec)] leading-relaxed">{result.summary}</p>
          )}

          {result.detectedBiases.length === 0 ? (
            <div className="bg-[var(--green-dim)] border border-[var(--green)] px-4 py-3">
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] font-medium">No significant bias patterns detected</p>
              <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
                The scoring patterns, participant distribution, and item selection look well-balanced for this session.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* High severity first */}
              {['high', 'medium', 'low'].map(sev =>
                result.detectedBiases
                  .filter(b => b.severity === sev)
                  .map(bias => (
                    <BiasInsightCard
                      key={bias.biasType}
                      bias={bias}
                      defaultExpanded={sev === 'high'}
                    />
                  ))
              )}
            </div>
          )}

          <p className="text-[10px] text-[var(--text-ter)] pt-1 border-t border-[var(--rule)]">
            Advisory only — AI analysis, not a guarantee. Use as a prompt for reflection, not a verdict.
          </p>
        </div>
      )}
    </div>
  );
}
